import { apiFetch } from "@d12labs/client";
import { Button } from "@d12labs/ui/button";
import { ConfirmDialog } from "@d12labs/ui/confirm-dialog";
import { PageHeader } from "@d12labs/ui/page-header";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, RefreshCw, Settings as SettingsIcon } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { composeSettingsSections, type SettingsSection } from "@/lib/settings-schema";
import { pluginManifests } from "../../../plugins/registry";
import type { SettingRowView } from "./settings/setting-row";
import { SettingSection } from "./settings/setting-section";
import { type NavItem, SettingsNav } from "./settings/settings-nav";

/** Live state returned by `GET /api/admin/settings` (one per declared key). */
type SettingView = {
	key: string;
	label: string;
	group: string;
	secret: boolean;
	restartRequired: boolean;
	floor: boolean;
	editable: boolean;
	source: "env" | "db" | "default";
	value: string | null;
	hasValue: boolean;
	options?: { value: string; label: string }[];
};

/** Sections the installed plugins contribute (from their manifests). */
function pluginSections(): SettingsSection[] {
	return pluginManifests
		.map((m) => m.settings)
		.filter((s): s is NonNullable<typeof s> => Boolean(s));
}

/** A section whose rows have been merged with live state + filtered by query. */
type ResolvedSection = SettingsSection & { resolvedRows: SettingRowView[] };

function buildEnvDump(sections: ResolvedSection[]): string {
	const lines: string[] = [];
	for (const section of sections) {
		lines.push(`# ${section.title}`);
		for (const row of section.resolvedRows) {
			// Secrets are never in hand on the client — emit a blank assignment.
			lines.push(`${row.key}=${row.secret ? "" : (row.value ?? "")}`);
		}
		lines.push("");
	}
	return lines.join("\n").trimEnd();
}

export function SettingsPage() {
	const qc = useQueryClient();
	const { data, error, isLoading } = useQuery({
		queryKey: ["admin", "settings"],
		queryFn: () => apiFetch<SettingView[]>("/api/admin/settings"),
		retry: false,
	});

	const [query, setQuery] = useState("");
	const [active, setActive] = useState("core");
	const scrollRef = useRef<HTMLDivElement>(null);
	const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

	// Merge the schema (sections + help) with live state (by key), then filter.
	const sections = useMemo<ResolvedSection[]>(() => {
		const views = new Map((data ?? []).map((v) => [v.key, v]));
		const q = query.trim().toLowerCase();
		const all = composeSettingsSections(pluginSections());
		return all
			.map((section) => {
				const resolvedRows: SettingRowView[] = [];
				for (const row of section.rows) {
					const view = views.get(row.key);
					if (!view) continue; // not returned by the API (shouldn't happen)
					if (
						q &&
						!row.label.toLowerCase().includes(q) &&
						!row.key.toLowerCase().includes(q) &&
						!(row.help ?? "").toLowerCase().includes(q)
					) {
						continue;
					}
					resolvedRows.push({
						key: row.key,
						label: row.label,
						help: row.help,
						placeholder: row.placeholder,
						secret: view.secret,
						restartRequired: view.restartRequired,
						floor: view.floor,
						editable: view.editable,
						source: view.source,
						value: view.value,
						hasValue: view.hasValue,
						options: view.options,
					});
				}
				return { ...section, resolvedRows };
			})
			.filter((s) => s.resolvedRows.length > 0);
	}, [data, query]);

	const navItems: NavItem[] = sections.map((s) => ({ id: s.id, title: s.title, icon: s.icon }));

	const jump = (id: string) => {
		setActive(id);
		const el = sectionRefs.current[id];
		const cont = scrollRef.current;
		if (el && cont) cont.scrollTo({ top: el.offsetTop - 16, behavior: "smooth" });
	};

	const onScroll = () => {
		const cont = scrollRef.current;
		if (!cont) return;
		const top = cont.scrollTop + 80;
		let cur = sections[0]?.id;
		for (const s of sections) {
			const el = sectionRefs.current[s.id];
			if (el && el.offsetTop <= top) cur = s.id;
		}
		if (cur) setActive(cur);
	};

	const onSaved = () => qc.invalidateQueries({ queryKey: ["admin", "settings"] });

	const copyEnv = async () => {
		try {
			await navigator.clipboard.writeText(buildEnvDump(sections));
			toast.success("Copied .env to clipboard (secrets blanked)");
		} catch {
			toast.error("Couldn't access the clipboard");
		}
	};

	if (error) {
		return (
			<>
				<PageHeader eyebrow="Admin" icon={SettingsIcon} title="Settings" />
				<div className="p-6">
					<p className="text-sm text-destructive">
						You don't have access to this page (set ADMIN_EMAILS to include your address).
					</p>
				</div>
			</>
		);
	}

	return (
		<>
			<PageHeader
				eyebrow="Admin"
				icon={SettingsIcon}
				title="Settings"
				subtitle="Runtime deployment config. A value set by an environment variable always wins and is read-only here."
				actions={
					<>
						<Button variant="ghost" size="sm" onClick={copyEnv}>
							<Copy className="size-3.5" />
							Copy .env
						</Button>
						<ConfirmDialog
							title="Restart the server?"
							description="Restart-required changes (OAuth credentials, the auth issuer) only take effect after a reboot. Restarts are handled by your host — redeploy, or restart the process — there is no in-app restart in the chassis."
							confirmLabel="I'll restart on the host"
							onConfirm={() => {
								toast.info("Restart your deployment to apply restart-required changes.");
							}}
						>
							<Button variant="secondary" size="sm">
								<RefreshCw className="size-3.5" />
								Restart server
							</Button>
						</ConfirmDialog>
					</>
				}
			/>

			<div className="grid min-h-0 flex-1 grid-cols-[240px_minmax(0,1fr)] overflow-hidden">
				<SettingsNav
					items={navItems}
					activeId={active}
					query={query}
					onQueryChange={setQuery}
					onJump={jump}
				/>

				<div ref={scrollRef} onScroll={onScroll} className="overflow-y-auto">
					<div className="mx-auto max-w-4xl space-y-9 px-8 py-7">
						{isLoading ? (
							<p className="text-sm text-muted-foreground">Loading…</p>
						) : sections.length === 0 ? (
							<div className="py-20 text-center">
								<p className="text-body-sm text-ink-subtle">No settings match “{query}”.</p>
							</div>
						) : (
							sections.map((section) => (
								<SettingSection
									key={section.id}
									ref={(el) => {
										sectionRefs.current[section.id] = el;
									}}
									section={section}
									rows={section.resolvedRows}
									onSaved={onSaved}
								/>
							))
						)}
						<div className="h-8" />
					</div>
				</div>
			</div>
		</>
	);
}
